import { Flex } from '@chakra-ui/react';
import { ScorecardBuilder } from '@kula-ai/components';
import { KText } from 'ui-library/components';
import { mapScorecardToData } from '@/components/shared/scorecard';
import { FileAttachmentPreviewReadonly } from '@/components/formBuilder/formFields/FormEditor/FileAttachmentPreviewReadonly';
import type { ScorecardType } from '@/types/interview';
import type { AttachmentType } from '@/types/attachment.interface';

const AttachmentPreviews = ({ files }: { files: AttachmentType[] }) => (
  <Flex flexDir="column" gap="10px" pt="8px">
    <Flex gap="12px" flexWrap="wrap">
      {files.map((file, index) => (
        <FileAttachmentPreviewReadonly key={index} file={file} />
      ))}
    </Flex>
  </Flex>
);

/**
 * Read-only scorecard view, built on the kula-ai `ScorecardBuilder` (disabled).
 * The five non-text answer types + the feedback/rating sections render natively;
 * `renderQuestion`/`renderFeedback` only kick in to append our file-attachment
 * previews under a question/feedback that has them — `renderDefault()` keeps the
 * native answer display, and returning `undefined` for everything else falls
 * back to native (no per-type re-implementation).
 */
export const InterviewScorecardReadOnly = ({
  scoreCard,
}: {
  scoreCard: ScorecardType;
}) => {
  if (!scoreCard?.id) {
    return <KText size="sm-medium">No scorecard data found</KText>;
  }

  return (
    <ScorecardBuilder
      disabled
      data={mapScorecardToData(scoreCard)}
      renderQuestion={({ index, renderDefault }) => {
        const attachments = scoreCard.questions?.[index]?.attachments ?? [];
        if (!attachments.length) return undefined;
        return (
          <>
            {renderDefault()}
            <AttachmentPreviews files={attachments} />
          </>
        );
      }}
      renderFeedback={({ renderDefault }) => {
        const attachments = scoreCard.feedback_attachments ?? [];
        if (!attachments.length) return undefined;
        return (
          <>
            {renderDefault()}
            <AttachmentPreviews files={attachments} />
          </>
        );
      }}
    />
  );
};
